import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import Panel from 'components/Panel/index';
import Button from 'components/Button';
import ReactSelect from 'components/Select/ReactSelect';
import CircleCheckbox from 'components/CircleCheckbox/CircleCheckbox';
import MessageBox from 'components/MessageBox';
import BarnToolsList from './BarnToolsList';
import BarnToolsMapping from './BarnToolsMapping';
// utils
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';
import handleFormErrors from 'utils/handleFormErrors';
import {
  IMPORTED_SENSOR_TYPES, fetchOrganizations, fetchSitesForOrg, toastBarnToolsErrors,
} from './barnToolsConstants';
// styles
import './BarnToolsIntegration.scss';

const initialState = {
  isLoading: false,
  showCreateForm: false,
  farms: [],
  selectedFarm: null,
  selectedBarn: null,
  organizations: [],
  selectedOrganization: null,
  sites: [],
  selectedSite: null,
  selectedSiteBarn: null,
  selectedSerials: [],
};

class BarnToolsIntegration extends Component {

  constructor(props) {
    super(props);
    this.state = initialState;
  }

  toggleCreateForm = () => {
    const { showCreateForm } = this.state;
    if (!showCreateForm) {
      this.getFarms();
      this.getOrganizations();
    }
    this.setState({ showCreateForm: !showCreateForm });
  };

  getFarms = () => {
    this.setState({ isLoading: true });

    fetchFromAPI('/admin/farms/search', {
      params: {
        per_page: 1000,
        active: true
      }
    })
      .then(({ resources }) => {
        this.setState({
          isLoading: false,
          farms: resources || [],
        });
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        return handleFormErrors(error);
      });
  };

  // Which BarnTools organizations this company's hardware sits in. Comes from
  // our own records, so opening the form costs no upstream calls.
  getOrganizations = () => {
    const { companyId } = this.props;
    if (!companyId) return;

    this.setState({ isLoading: true });

    fetchOrganizations(fetchFromAPI, companyId)
      .then((organizations) => this.setState({ isLoading: false, organizations }))
      .catch(() => {
        this.setState({ isLoading: false, organizations: [] });
        showToastrMessage('component.toastr.barnTools.error.inventoryFailed', 'error');
      });
  };

  // A credential only ever sees its own organization, so sites are fetched
  // once one is chosen rather than all at once.
  handleOrganizationChange = (option) => {
    const { companyId } = this.props;

    this.setState((prevState) => ({
      selectedOrganization: option
        ? prevState.organizations.find((org) => org.id === option.value)
        : null,
      sites: [],
      selectedSite: null,
      selectedSiteBarn: null,
      selectedSerials: [],
    }));

    if (!option) return;

    this.setState({ isLoading: true });

    fetchSitesForOrg(fetchFromAPI, companyId, { credentialId: option.value })
      .then((sites) => this.setState({ isLoading: false, sites }))
      .catch(() => {
        this.setState({ isLoading: false, sites: [] });
        showToastrMessage('component.toastr.barnTools.error.inventoryFailed', 'error');
      });
  };

  handleFarmChange = (option) => {
    this.setState((prevState) => {
      const selectedFarm = option ? prevState.farms.find((farm) => farm.id === option.value) : null;

      if (selectedFarm) {
        this.getFarmBarns(selectedFarm.id);
      }

      return { selectedFarm, selectedBarn: null };
    });
  };

  getFarmBarns = (farmId) => {
    fetchFromAPI(`/farms/${farmId}/barns`)
      .then(({ resources }) => {
        this.setState((prevState) => ({
          selectedFarm: {
            ...prevState.selectedFarm,
            barns: resources || [],
          },
        }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch barns:', error);
        this.setState((prevState) => ({
          selectedFarm: {
            ...prevState.selectedFarm,
            barns: [],
          },
        }));
      });
  };

  handleBarnChange = (option) => {
    this.setState((prevState) => ({
      selectedBarn: option
        ? prevState.selectedFarm?.barns?.find((barn) => barn.id === option.value)
        : null,
    }));
  };

  handleSiteChange = (option) => {
    this.setState((prevState) => {
      const selectedSite = option ? prevState.sites.find((site) => site.id === option.value) : null;
      // BarnTools names most sites after their only barn, so pick it for them.
      const barns = selectedSite?.barns || [];

      return {
        selectedSite,
        selectedSiteBarn: barns.length === 1 ? barns[0] : null,
        selectedSerials: [],
      };
    });
  };

  handleSiteBarnChange = (option) => {
    this.setState((prevState) => ({
      selectedSiteBarn: option
        ? prevState.selectedSite?.barns?.find((barn) => barn.id === option.value)
        : null,
      selectedSerials: [],
    }));
  };

  toggleSerial = (serialNumber) => {
    this.setState((prevState) => ({
      selectedSerials: prevState.selectedSerials.includes(serialNumber)
        ? prevState.selectedSerials.filter((serial) => serial !== serialNumber)
        : [...prevState.selectedSerials, serialNumber],
    }));
  };

  importableSensors = () => {
    const { selectedSiteBarn } = this.state;

    return (selectedSiteBarn?.sensors || []).filter((sensor) => IMPORTED_SENSOR_TYPES.includes(sensor.typeName));
  };

  isSubmittable = () => {
    const { selectedFarm, selectedBarn, selectedOrganization, selectedSite, selectedSerials } = this.state;

    return Boolean(selectedFarm && selectedBarn && selectedOrganization && selectedSite && selectedSerials.length);
  };

  handleSubmit = () => {
    const { companyId } = this.props;
    const { selectedBarn, selectedOrganization, selectedSite, selectedSiteBarn, selectedSerials } = this.state;

    if (!companyId) {
      showToastrMessage('component.toastr.barnTools.error.noCompany');
      return;
    }

    if (!this.isSubmittable()) {
      showToastrMessage('component.toastr.barnTools.error.missingFields');
      return;
    }

    this.setState({ isLoading: true });

    const integrationData = {
      barn_id: selectedBarn.id,
      bt_org_name: selectedOrganization.org_name,
      bt_site_id: selectedSite.id,
      bt_barn_id: selectedSiteBarn?.id || null,
      sensor_serials: selectedSerials,
    };

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations`, {
      method: 'POST',
      body: JSON.stringify({ integration: integrationData }),
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.integrationCreated');
        this.setState(initialState);
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        toastBarnToolsErrors(error, 'component.toastr.barnTools.error.createFailed');
      });
  };

  handleCancel = () => {
    this.setState(initialState);
  };

  renderSensorPicker() {
    const { selectedSerials } = this.state;
    const sensors = this.importableSensors();

    if (!sensors.length) {
      return (
        <div className="no-sensors">
          <FormattedMessage id="component.barnTools.noSensorsOnBarn" />
        </div>
      );
    }

    return (
      <div className="sensor-picker">
        {sensors.map((sensor) => (
          <CircleCheckbox
            key={sensor.serialNumber}
            className="sensor-option"
            checked={selectedSerials.includes(sensor.serialNumber)}
            onChange={() => this.toggleSerial(sensor.serialNumber)}
            label={sensor.name || sensor.serialNumber}
            subLabel={`${sensor.typeName} · ${sensor.serialNumber}`}
          />
        ))}
      </div>
    );
  }

  render() {
    const { companyId } = this.props;
    const {
      isLoading,
      showCreateForm,
      farms,
      selectedFarm,
      selectedBarn,
      organizations,
      selectedOrganization,
      sites,
      selectedSite,
      selectedSiteBarn,
    } = this.state;

    if (!companyId) {
      return (
        <div className="BarnToolsIntegration">
          <div className="error-message">
            <FormattedMessage id="component.barnTools.error.noCompany" />
          </div>
        </div>
      );
    }

    const farmOptions = farms.map((farm) => ({
      value: farm.id,
      label: farm.name,
    }));

    const barnOptions = selectedFarm?.barns?.map((barn) => ({
      value: barn.id,
      label: barn.name,
    })) || [];

    const organizationOptions = organizations.map((org) => ({ value: org.id, label: org.org_name }));

    const siteOptions = sites.map((site) => ({ value: site.id, label: site.name }));

    const siteBarnOptions = selectedSite?.barns?.map((barn) => ({
      value: barn.id,
      label: barn.name,
    })) || [];

    return (
      <div className="BarnToolsIntegration">
        {/* Warning Message */}
        <MessageBox type="warning" className="mb-20">
          <FormattedMessage id="component.barnTools.warning.dataAvailability" />
        </MessageBox>

        {/* Header with Create Button */}
        <div className="barn-tools-header">
          <Button
            onClick={this.toggleCreateForm}
            primary
            disabled={showCreateForm}
          >
            <FormattedMessage id="component.barnTools.createNew" />
          </Button>
        </div>

        {/* Create New Integration Form */}
        {showCreateForm && (
          <Panel className="mb-30">
            <Panel.Body>
              <div className="row">
                <div className="small-12 medium-8 large-6 column">

                  <div className="form-section-title">
                    <FormattedMessage id="component.barnTools.everyPigLocation" />
                  </div>

                  <div className="form-group">
                    <label>
                      <FormattedMessage id="component.barnTools.farm" />
                    </label>
                    <ReactSelect
                      options={farmOptions}
                      value={selectedFarm ? { value: selectedFarm.id, label: selectedFarm.name } : null}
                      onChange={this.handleFarmChange}
                      placeholder={<FormattedMessage id="component.barnTools.selectFarm" />}
                      noInputMargin
                      clearable={false}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FormattedMessage id="component.barnTools.barn" />
                    </label>
                    <ReactSelect
                      options={barnOptions}
                      value={selectedBarn ? { value: selectedBarn.id, label: selectedBarn.name } : null}
                      onChange={this.handleBarnChange}
                      placeholder={<FormattedMessage id="component.barnTools.selectBarn" />}
                      noInputMargin
                      clearable={false}
                      searchable
                      disabled={!selectedFarm}
                    />
                  </div>

                  <div className="form-section-title">
                    <FormattedMessage id="component.barnTools.barnToolsHardware" />
                  </div>

                  <div className="form-group">
                    <label>
                      <FormattedMessage id="component.barnTools.organization" />
                    </label>
                    <ReactSelect
                      options={organizationOptions}
                      value={selectedOrganization
                        ? { value: selectedOrganization.id, label: selectedOrganization.org_name }
                        : null}
                      onChange={this.handleOrganizationChange}
                      placeholder={<FormattedMessage id="component.barnTools.selectOrganization" />}
                      noInputMargin
                      clearable={false}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FormattedMessage id="component.barnTools.site" />
                    </label>
                    <ReactSelect
                      options={siteOptions}
                      value={selectedSite ? siteOptions.find((o) => o.value === selectedSite.id) : null}
                      onChange={this.handleSiteChange}
                      placeholder={<FormattedMessage id="component.barnTools.selectSite" />}
                      noInputMargin
                      clearable={false}
                      searchable
                      disabled={!selectedOrganization}
                    />
                  </div>

                  {siteBarnOptions.length > 1 && (
                    <div className="form-group">
                      <label>
                        <FormattedMessage id="component.barnTools.siteBarn" />
                      </label>
                      <ReactSelect
                        options={siteBarnOptions}
                        value={selectedSiteBarn
                          ? { value: selectedSiteBarn.id, label: selectedSiteBarn.name }
                          : null}
                        onChange={this.handleSiteBarnChange}
                        placeholder={<FormattedMessage id="component.barnTools.selectSiteBarn" />}
                        noInputMargin
                        clearable={false}
                        searchable
                      />
                    </div>
                  )}

                  {selectedSiteBarn && (
                    <div className="form-group">
                      <label>
                        <FormattedMessage id="component.barnTools.sensors" />
                      </label>
                      {this.renderSensorPicker()}
                    </div>
                  )}

                  <div className="form-actions">
                    <Button
                      onClick={this.handleSubmit}
                      disabled={!this.isSubmittable()}
                      primary
                      className="mr-10"
                    >
                      <FormattedMessage id="component.barnTools.createIntegration" />
                    </Button>
                    <Button
                      onClick={this.handleCancel}
                      light
                    >
                      <FormattedMessage id="general.button.cancel" />
                    </Button>
                  </div>

                </div>
              </div>
            </Panel.Body>
          </Panel>
        )}

        {/* Devices still waiting to be pointed at the new API */}
        <BarnToolsMapping companyId={companyId} />

        {/* Existing Integrations List */}
        <Panel className="mt-30">
          <Panel.Heading title={<FormattedMessage id="component.barnTools.existingIntegrations.title" />} />
          <Panel.Body noPadding>
            <BarnToolsList companyId={companyId} />
          </Panel.Body>
        </Panel>

        {/* Loading Overlay */}
        {isLoading && <Preloader />}
      </div>
    );
  }
}

BarnToolsIntegration.propTypes = {
  companyId: T.number,
};

const mapStateToProps = (state) => ({
  companyId: state.auth.user?.current_company?.id,
});

export default connect(mapStateToProps)(BarnToolsIntegration);
